import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { supabase } from "../config/supabase";
import Settings from "./Settings";
import Calendar from "./Calendar";
import Statistics from "./Statistics";
import LogEntry from "./LogEntry";
import GoalsManagement from "./GoalsManagement";
import PatientCalendarView from "./PatientCalendarView";
import PatientStatisticsView from "./PatientStatisticsView";
import DoctorInvitations from "./DoctorInvitations";
import AdminInvitations from "./AdminInvitations";
import AdminUsers from "./AdminUsers";
import Messages from "./Messages";
import ErrorReports from "./ErrorReports";
import ProgressDashboard from "./ProgressDashboard";
import { addDataRefreshListener } from "../utils/events";

const Dashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<{
    name: string;
    surname: string;
    account_type: string;
    treatment_type?: string;
  } | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: "",
    surname: "",
    treatmentType: "self" as "self" | "child",
  });
  const [profileStatus, setProfileStatus] = useState<string>("");
  const [profileGoalHours, setProfileGoalHours] = useState("4");
  const [profileGoalMinutes, setProfileGoalMinutes] = useState("0");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileLoadingInitial, setProfileLoadingInitial] = useState(true);
  const [profileLoadingError, setProfileLoadingError] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [currentPage, setCurrentPage] = useState<string>("dashboard");
  const [refreshKey, setRefreshKey] = useState(0);

  const location = useLocation();

  // Helper function to process name input (capitalize and remove numbers)
  const processNameInput = (value: string): string => {
    // Remove any numbers and special characters except spaces, hyphens, and apostrophes
    const cleaned = value.replace(/[^A-Za-z\s\-']/g, "");
    // Capitalize first letter of each word
    return cleaned
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Handle URL parameters for tab navigation
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get("tab");
    if (tab === "children") {
      setCurrentPage("settings");
      // You might need to pass this to Settings component or handle it there
    }
  }, [location]);

  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("darkMode");
      return saved ? JSON.parse(saved) : false;
    } catch {
      // If localStorage fails or data is corrupted, default to false
      return false;
    }
  });
  const navigate = useNavigate();

  // Extract first/last name from a Supabase user object.
  // Searches user_metadata AND every identity's identity_data — Apple often stores
  // the name only in identities[].identity_data, not in user_metadata.
  // Handles all known formats:
  //   Apple nested:  { name: { firstName: "John", lastName: "Doe" } }
  //   Apple flat:    { full_name: "John Doe" }
  //   Google:        { given_name: "John", family_name: "Doe" }
  const extractOAuthName = (user: {
    user_metadata?: Record<string, unknown>;
    identities?: Array<{ identity_data?: Record<string, unknown> }>;
  } | null | undefined): { firstName: string; lastName: string } => {
    if (!user) return { firstName: "", lastName: "" };

    // Build ordered list of all data sources to search
    const sources: Record<string, unknown>[] = [];
    if (user.user_metadata) sources.push(user.user_metadata);
    for (const identity of user.identities || []) {
      if (identity.identity_data) sources.push(identity.identity_data);
    }

    for (const src of sources) {
      // Apple nested object: src.name = { firstName: "...", lastName: "..." }
      const nameField = src.name;
      if (nameField && typeof nameField === "object" && !Array.isArray(nameField)) {
        const nested = nameField as Record<string, string>;
        const first = (nested.firstName || "").trim();
        const last = (nested.lastName || "").trim();
        if (first || last) return { firstName: first, lastName: last };
      }

      // Flat string: full_name or name as string
      const fullNameStr =
        (src.full_name as string) ||
        (typeof src.name === "string" ? src.name : "") ||
        "";

      // Google style or fallback
      const firstName =
        (src.given_name as string) ||
        (src.first_name as string) ||
        fullNameStr.split(" ")[0] ||
        "";
      const lastName =
        (src.family_name as string) ||
        (src.last_name as string) ||
        fullNameStr.split(" ").slice(1).join(" ") ||
        "";

      if (firstName || lastName) {
        return { firstName: firstName.trim(), lastName: lastName.trim() };
      }
    }

    return { firstName: "", lastName: "" };
  };

  const checkProfile = async () => {
    try {
      setProfileLoadingInitial(true);
      setProfileLoadingError(false);

      // Add timeout to prevent infinite loading
      const timeoutId = setTimeout(() => {
        setProfileLoadingError(true);
        setProfileLoadingInitial(false);
      }, 10000); // 10 second timeout

      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        setUser(data.user);

        // Try to get existing profile
        let { data: profileData, error: fetchError } = await supabase
          .from("user_data")
          .select("name, surname, account_type, treatment_type")
          .eq("user_id", data.user.id)
          .maybeSingle();

        // If no profile exists, create one
        if (!profileData && !fetchError) {
          const { data: newProfile, error: createError } = await supabase
            .from("user_data")
            .insert([
              {
                user_id: data.user.id,
                email: data.user.email,
                name: null,
                surname: null,
                account_type: "user",
                treatment_type: "self", // Default to "self" - can be changed in settings
              },
            ])
            .select("name, surname, account_type, treatment_type, email")
            .maybeSingle();

          if (createError) {
            console.error("Error creating profile:", createError);
            // Set a default profile to prevent infinite loading
            profileData = {
              name: null,
              surname: null,
              account_type: "user",
              treatment_type: "self",
            };
          } else {
            profileData = newProfile;
          }
        } else if (fetchError && fetchError.code !== "PGRST116") {
          // If there's an error fetching profile (not just "not found"), log it
          console.error("Error fetching profile:", fetchError);
          // Set a default profile to prevent infinite loading
          profileData = {
            name: null,
            surname: null,
            account_type: "user",
            treatment_type: "self",
          };
        }

        setProfile(profileData);
        if (profileData) {
          // Pre-fill from OAuth provider metadata (Apple/Google) when name is missing.
          // Searches user_metadata AND identities[].identity_data (Apple uses the latter).
          const oauthName =
            !profileData.name || !profileData.surname
              ? extractOAuthName(data.user)
              : { firstName: "", lastName: "" };
          setProfileForm({
            name: profileData.name || oauthName.firstName,
            surname: profileData.surname || oauthName.lastName,
            treatmentType: profileData.treatment_type || "self",
          });
        }
      } else {
        navigate("/login");
      }

      clearTimeout(timeoutId);
    } catch (error) {
      console.error("Error in checkProfile:", error);
      setProfileLoadingError(true);
    } finally {
      setProfileLoadingInitial(false);
    }
  };

  // Listen for auth state changes and redirect immediately if session is lost
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // If session is lost (e.g., during logout), immediately redirect
      if (!session || !session.user) {
        setIsLoggingOut(true);
        setUser(null);
        setProfile(null);
        navigate("/login", { replace: true });
        return;
      }

      // When OAuth redirect completes and session is established, the SIGNED_IN event
      // fires — this may happen AFTER checkProfile() already ran with no session.
      // Pre-fill the profile form from provider metadata (handles both Apple and Google).
      if (event === "SIGNED_IN") {
        setUser(session.user);
        // Pass the full user so we can search identities[] as well as user_metadata
        const oauthName = extractOAuthName(session.user);
        if (oauthName.firstName || oauthName.lastName) {
          setProfileForm((prev) => ({
            ...prev,
            name: prev.name || oauthName.firstName,
            surname: prev.surname || oauthName.lastName,
          }));
        }
      }
    });

    checkProfile();

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Global data refresh: bump key to force rerender of key pages
  useEffect(() => {
    const remove = addDataRefreshListener((event) => {
      setRefreshKey((k) => k + 1);
      // If profile was updated, refetch profile data
      if (event.detail?.reason === "profile_updated" && user) {
        checkProfile();
      }
    });
    return remove;
  }, [user]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (mobileMenuOpen && !target.closest(".mobile-menu-container")) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    // Set logging out state to prevent any profile loading
    setIsLoggingOut(true);

    // Immediately navigate to login to prevent any flash of profile completion screen
    navigate("/login");

    // Clean up state after navigation to prevent any API calls
    setUser(null);
    setProfile(null);
    setProfileLoadingInitial(false);
    setProfileLoadingError(false);
    setCurrentPage("dashboard");

    // Fast local sign-out to avoid slow/cross-origin global revocation
    try {
      await supabase.auth.signOut({ scope: "local" } as any);
    } catch {
      // ignore
    }
    // Fire-and-forget global sign-out; don't block UI or fail on CORS
    setTimeout(() => {
      // @ts-ignore allow best-effort; some environments block this request
      supabase.auth.signOut({ scope: "global" }).catch(() => {});
    }, 0);
  };

  // Helper for avatar/initials
  const getInitials = () => {
    if (profile?.name && profile?.surname) {
      return (
        profile.name.charAt(0).toUpperCase() +
        profile.surname.charAt(0).toUpperCase()
      );
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  // Check if user is a doctor
  const isDoctor = profile?.account_type === "doctor";
  const isAdmin = profile?.account_type === "admin";

  // Input validation
  const validateName = (name: string): boolean => {
    return name.trim().length >= 2 && /^[a-zA-Z\s-']+$/.test(name.trim());
  };

  // Profile completion form submit
  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileStatus("");

    // Validate inputs
    if (!validateName(profileForm.name)) {
      setProfileStatus(
        "Name must be at least 2 characters and contain only letters."
      );
      setProfileLoading(false);
      return;
    }

    if (!validateName(profileForm.surname)) {
      setProfileStatus(
        "Surname must be at least 2 characters and contain only letters."
      );
      setProfileLoading(false);
      return;
    }

    // Treatment type is always "self" by default - can be changed in settings later
    // Ensure treatmentType is set to "self" if not already set
    if (!profileForm.treatmentType) {
      profileForm.treatmentType = "self";
    }

    try {
      if (!user) throw new Error("No user");

      // Check if profile exists first
      const { data: existingProfile } = await supabase
        .from("user_data")
        .select("id, name, surname, treatment_type")
        .eq("user_id", user.id)
        .maybeSingle();

      let updateResult;

      if (existingProfile) {
        // Profile exists, update it
        updateResult = await supabase
          .from("user_data")
          .update({
            name: profileForm.name.trim(),
            surname: profileForm.surname.trim(),
            treatment_type: profileForm.treatmentType || "self", // Default to "self" if not set
          })
          .eq("user_id", user.id)
          .select();
      } else {
        // Profile doesn't exist, create new one
        updateResult = await supabase
          .from("user_data")
          .insert([
            {
              user_id: user.id,
              email: user.email,
              name: profileForm.name.trim(),
              surname: profileForm.surname.trim(),
              account_type: "user",
              treatment_type: profileForm.treatmentType || "self", // Default to "self" if not set
            },
          ])
          .select();
      }

      if (updateResult.error) {
        throw updateResult.error;
      }

      // Save the daily goal from profile completion (applies to all days)
      const hours = parseInt(profileGoalHours, 10) || 0;
      const mins = parseInt(profileGoalMinutes, 10) || 0;
      const dailyGoal = Math.min(hours * 60 + mins, 480);
      if (dailyGoal >= 30) {
        await supabase.from("user_goals").upsert(
          {
            user_id: user.id,
            monday_goal_minutes: dailyGoal,
            tuesday_goal_minutes: dailyGoal,
            wednesday_goal_minutes: dailyGoal,
            thursday_goal_minutes: dailyGoal,
            friday_goal_minutes: dailyGoal,
            saturday_goal_minutes: dailyGoal,
            sunday_goal_minutes: dailyGoal,
            weekly_goal_minutes: dailyGoal * 7,
          },
          { onConflict: "user_id" }
        );
        // Snapshot initial goals into history
        await supabase.from("goal_history").upsert({
          user_id: user.id,
          child_id: null,
          effective_from: new Date().toISOString().split("T")[0],
          sunday_goal_minutes: dailyGoal,
          monday_goal_minutes: dailyGoal,
          tuesday_goal_minutes: dailyGoal,
          wednesday_goal_minutes: dailyGoal,
          thursday_goal_minutes: dailyGoal,
          friday_goal_minutes: dailyGoal,
          saturday_goal_minutes: dailyGoal,
        }, { onConflict: "user_id,child_id,effective_from" });
      }

      setProfileStatus("Profile updated successfully!");

      // Reload profile
      const { data: profileData, error: reloadError } = await supabase
        .from("user_data")
        .select("name, surname, account_type, treatment_type")
        .eq("user_id", user.id)
        .maybeSingle();

      if (reloadError || !profileData) {
        // Failed to reload profile - error already handled
      } else {
        setProfile(profileData);
        // Update the form with the new data
        setProfileForm({
          name: (profileData && profileData.name) || "",
          surname: (profileData && profileData.surname) || "",
          treatmentType: (profileData && profileData.treatment_type) || "self",
        });
      }
    } catch (err: any) {
      // Error handling - details logged only in development
      setProfileStatus(`Failed to update profile: ${err.message}`);
    } finally {
      setProfileLoading(false);
    }
  };

  // Show profile completion form if name or surname is empty, but only after initial loading is complete
  // Don't show if user is logging out
  const needsProfile =
    !profileLoadingInitial &&
    !isLoggingOut &&
    !profileLoadingError &&
    (!profile?.name ||
      !profile?.surname ||
      profile?.name === null ||
      profile?.surname === null);

  // Dark mode toggle handler
  const handleDarkModeToggle = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);

    try {
      localStorage.setItem("darkMode", JSON.stringify(newDarkMode));
    } catch {
      // If localStorage fails, continue without saving preference
      // Silently fail - not critical
    }
  };

  // Profile update handler for Settings component
  const handleProfileUpdate = (updatedProfile: any) => {
    setProfile(updatedProfile);
    setProfileForm({
      name: updatedProfile.name || "",
      surname: updatedProfile.surname || "",
      treatmentType: updatedProfile.treatment_type || "self",
    });
  };

  // Early return: Don't render content if logging out or no user (prevents empty page flash)
  if (isLoggingOut || (!user && !profileLoadingInitial)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 shadow-lg">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-brand-dark-blue"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="ml-3 text-brand-dark-blue font-medium">
                {isLoggingOut ? "Logging out..." : "Loading..."}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${
        darkMode
          ? "bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green text-white"
          : "bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green"
      }`}
    >
      {/* Navbar */}
      <header
        className={`${
          darkMode ? "bg-gray-700 shadow-gray-900" : "bg-white shadow-md"
        } w-full transition-colors duration-300 fixed top-0 left-0 right-0 z-[1000]`}
      >
        <nav className="container mx-auto flex items-center justify-between py-3 px-4 md:px-8 relative mobile-menu-container">
          {/* Logo */}
          <button
            onClick={() => setCurrentPage("dashboard")}
            className="flex items-center space-x-2 cursor-pointer"
          >
            <img
              src={
                darkMode
                  ? "/assets/Amblify_logo_balts.png"
                  : "/assets/Amblify_logo_zilsfix.png"
              }
              alt="Amblyfy"
              className="h-10"
            />
          </button>
          {/* Desktop Nav */}
          <div className="hidden md:flex space-x-1 lg:space-x-2 absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => setCurrentPage("dashboard")}
              className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                currentPage === "dashboard"
                  ? "text-[#cad76a]"
                  : darkMode
                  ? "text-white hover:text-gray-100"
                  : "text-gray-700 hover:text-brand-dark-blue"
              }`}
            >
              Dashboard
            </button>
            {!isAdmin &&
              !isDoctor && ( // Added !isDoctor condition
                <button
                  onClick={() => setCurrentPage("log-progress")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "log-progress"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Log Progress
                </button>
              )}
            {isDoctor && (
              <>
                <button
                  onClick={() => setCurrentPage("patients")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "patients"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Patients
                </button>
                <button
                  onClick={() => setCurrentPage("patient-calendar")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "patient-calendar"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Patient Calendar
                </button>
                <button
                  onClick={() => setCurrentPage("patient-stats")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "patient-stats"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Patient Stats
                </button>
              </>
            )}
            {!isDoctor && !isAdmin && (
              <>
                <button
                  onClick={() => setCurrentPage("calendar")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "calendar"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Calendar
                </button>
                <button
                  onClick={() => setCurrentPage("healthcare")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "healthcare"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Healthcare Team
                </button>
                <button
                  onClick={() => setCurrentPage("statistics")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "statistics"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Statistics
                </button>
              </>
            )}
            {isAdmin && (
              <>
                <button
                  onClick={() => setCurrentPage("admin-invitations")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "admin-invitations"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Send Invitations
                </button>
                <button
                  onClick={() => setCurrentPage("admin-users")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "admin-users"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Users
                </button>
                <button
                  onClick={() => setCurrentPage("messages")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "messages"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Messages
                </button>
                <button
                  onClick={() => setCurrentPage("error-reports")}
                  className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                    currentPage === "error-reports"
                      ? "text-[#cad76a]"
                      : darkMode
                      ? "text-white hover:text-gray-100"
                      : "text-gray-700 hover:text-brand-dark-blue"
                  }`}
                >
                  Error Reports
                </button>
              </>
            )}

            <button
              onClick={() => setCurrentPage("settings")}
              className={`font-extrabold transition-colors rounded-md px-2 py-2 text-xs lg:text-sm ${
                currentPage === "settings"
                  ? "text-[#cad76a]"
                  : darkMode
                  ? "text-white hover:text-gray-100"
                  : "text-gray-700 hover:text-brand-dark-blue"
              }`}
            >
              Settings
            </button>
          </div>

          {/* User Info & Logout */}
          <div className="hidden md:flex items-center space-x-3">
            <div className="flex items-center">
              <div className="w-9 h-9 rounded-full bg-brand-cyan flex items-center justify-center text-white font-bold text-lg shadow">
                {getInitials()}
              </div>
            </div>
            <button
              onClick={handleLogout}
              className={`font-medium px-3 py-1.5 rounded-md shadow-sm transition-colors text-xs lg:text-sm ${
                darkMode
                  ? "bg-gray-100 text-gray-800 hover:bg-white"
                  : "bg-brand-dark-blue text-white hover:bg-brand-cyan"
              }`}
            >
              Log Out
            </button>
          </div>
          {/* Hamburger for mobile */}
          <button
            className="md:hidden flex items-center justify-center p-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-cyan mobile-menu-container relative z-[1101]"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label="Toggle menu"
          >
            <svg
              className={`w-7 h-7 ${
                darkMode ? "text-white" : "text-brand-dark-blue"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div
              className={`absolute top-full left-0 w-full shadow-lg rounded-b-xl py-4 px-4 flex flex-col space-y-2 md:hidden animate-fade-in z-[1100] border-t mobile-menu-container ${
                darkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-white border-gray-200"
              }`}
            >
              <button
                onClick={() => {
                  setCurrentPage("dashboard");
                  setMobileMenuOpen(false);
                }}
                className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                  currentPage === "dashboard"
                    ? "text-[#cad76a]"
                    : darkMode
                    ? "text-white hover:text-gray-100"
                    : "text-gray-700 hover:text-brand-dark-blue"
                }`}
              >
                Dashboard
              </button>
              <button
                onClick={() => {
                  setCurrentPage("log-progress");
                  setMobileMenuOpen(false);
                }}
                className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                  currentPage === "log-progress"
                    ? "text-[#cad76a]"
                    : darkMode
                    ? "text-white hover:text-gray-100"
                    : "text-gray-700 hover:text-brand-dark-blue"
                }`}
              >
                Log Progress
              </button>
              {isDoctor && (
                <>
                  <button
                    onClick={() => {
                      setCurrentPage("patients");
                      setMobileMenuOpen(false);
                    }}
                    className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                      currentPage === "patients"
                        ? "text-[#cad76a]"
                        : darkMode
                        ? "text-white hover:text-gray-100"
                        : "text-gray-700 hover:text-brand-dark-blue"
                    }`}
                  >
                    Patients
                  </button>
                  <button
                    onClick={() => {
                      setCurrentPage("patient-calendar");
                      setMobileMenuOpen(false);
                    }}
                    className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                      currentPage === "patient-calendar"
                        ? "text-[#cad76a]"
                        : darkMode
                        ? "text-white hover:text-gray-100"
                        : "text-gray-700 hover:text-brand-dark-blue"
                    }`}
                  >
                    Patient Calendar
                  </button>
                  <button
                    onClick={() => {
                      setCurrentPage("patient-stats");
                      setMobileMenuOpen(false);
                    }}
                    className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                      currentPage === "patient-stats"
                        ? "text-[#cad76a]"
                        : darkMode
                        ? "text-white hover:text-gray-100"
                        : "text-gray-700 hover:text-brand-dark-blue"
                    }`}
                  >
                    Patient Stats
                  </button>
                </>
              )}
              {isAdmin && (
                <>
                  <button
                    onClick={() => {
                      setCurrentPage("admin-invitations");
                      setMobileMenuOpen(false);
                    }}
                    className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                      currentPage === "admin-invitations"
                        ? "text-[#cad76a]"
                        : darkMode
                        ? "text-white hover:text-gray-100"
                        : "text-gray-700 hover:text-brand-dark-blue"
                    }`}
                  >
                    Send Invitations
                  </button>
                  <button
                    onClick={() => {
                      setCurrentPage("messages");
                      setMobileMenuOpen(false);
                    }}
                    className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                      currentPage === "messages"
                        ? "text-[#cad76a]"
                        : darkMode
                        ? "text-white hover:text-gray-100"
                        : "text-gray-700 hover:text-brand-dark-blue"
                    }`}
                  >
                    Messages
                  </button>
                  <button
                    onClick={() => {
                      setCurrentPage("error-reports");
                      setMobileMenuOpen(false);
                    }}
                    className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                      currentPage === "error-reports"
                        ? "text-[#cad76a]"
                        : darkMode
                        ? "text-white hover:text-gray-100"
                        : "text-gray-700 hover:text-brand-dark-blue"
                    }`}
                  >
                    Error Reports
                  </button>
                </>
              )}
              {!isDoctor && !isAdmin && (
                <>
                  <button
                    onClick={() => {
                      setCurrentPage("calendar");
                      setMobileMenuOpen(false);
                    }}
                    className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                      currentPage === "calendar"
                        ? "text-[#cad76a]"
                        : darkMode
                        ? "text-white hover:text-gray-100"
                        : "text-gray-700 hover:text-brand-dark-blue"
                    }`}
                  >
                    Calendar
                  </button>
                  <button
                    onClick={() => {
                      setCurrentPage("statistics");
                      setMobileMenuOpen(false);
                    }}
                    className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                      currentPage === "statistics"
                        ? "text-[#cad76a]"
                        : darkMode
                        ? "text-white hover:text-gray-100"
                        : "text-gray-700 hover:text-brand-dark-blue"
                    }`}
                  >
                    Statistics
                  </button>
                </>
              )}

              <button
                onClick={() => {
                  setCurrentPage("settings");
                  setMobileMenuOpen(false);
                }}
                className={`font-extrabold transition-colors rounded-md px-3 py-2 text-base ${
                  currentPage === "settings"
                    ? "text-[#cad76a]"
                    : darkMode
                    ? "text-white hover:text-gray-100"
                    : "text-gray-700 hover:text-brand-dark-blue"
                }`}
              >
                Settings
              </button>
              <div className="flex items-center space-x-2 mt-2">
                <div className="w-9 h-9 rounded-full bg-brand-cyan flex items-center justify-center text-white font-bold text-lg shadow">
                  {getInitials()}
                </div>
                <span
                  className={`font-semibold text-base ${
                    darkMode ? "text-white" : "text-brand-dark-blue"
                  }`}
                >
                  {profile?.name && profile?.surname
                    ? `${profile.name} ${profile.surname}`
                    : user?.email}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className={`w-full font-medium px-3 py-1.5 rounded-md shadow-sm transition-colors mt-2 ${
                  darkMode
                    ? "bg-gray-100 text-gray-800 hover:bg-white"
                    : "bg-brand-dark-blue text-white hover:bg-brand-cyan"
                }`}
              >
                Log Out
              </button>
            </div>
          )}
        </nav>
      </header>
      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center overflow-auto relative z-10 pt-16">
        {/* Initial Loading State */}
        {profileLoadingInitial && !isLoggingOut && (
          <div className="flex items-center justify-center w-full h-full">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="flex flex-col items-center">
                {profileLoadingError ? (
                  <>
                    <div className="flex items-center justify-center mb-4">
                      <svg
                        className="h-8 w-8 text-red-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                      <span className="ml-3 text-red-500 font-medium">
                        Failed to load profile
                      </span>
                    </div>
                    <p className="text-gray-600 text-center mb-4">
                      There was an error loading your profile. Please try again.
                    </p>
                  </>
                ) : (
                  <div className="flex items-center justify-center mb-4">
                    <svg
                      className="animate-spin h-8 w-8 text-brand-dark-blue"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span className="ml-3 text-brand-dark-blue font-medium">
                      Loading your profile...
                    </span>
                  </div>
                )}
                <button
                  onClick={async () => {
                    setIsLoggingOut(true);
                    await supabase.auth.signOut();
                    navigate("/login");
                  }}
                  className="px-4 py-2 bg-brand-dark-blue text-white rounded-lg hover:bg-brand-cyan transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logout Loading State */}
        {isLoggingOut && (
          <div className="flex items-center justify-center w-full h-full">
            <div className="bg-white rounded-lg p-8 shadow-lg">
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center mb-4">
                  <svg
                    className="animate-spin h-8 w-8 text-brand-dark-blue"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span className="ml-3 text-brand-dark-blue font-medium">
                    Logging out...
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Main Content - Only show when not loading and not logging out */}
        {!profileLoadingInitial && !isLoggingOut && (
          <>
            {/* Settings Page */}
            <div className={currentPage === "settings" ? "block" : "hidden"}>
              <Settings
                user={user}
                profile={profile}
                onProfileUpdate={handleProfileUpdate}
                onLogout={handleLogout}
                darkMode={darkMode}
                onDarkModeToggle={handleDarkModeToggle}
                initialTab={
                  location.search.includes("tab=children")
                    ? "children"
                    : "profile"
                }
              />
            </div>

            {/* Admin Invitations Page */}
            {isAdmin && (
              <div
                className={
                  currentPage === "admin-invitations" ? "block" : "hidden"
                }
              >
                <div className="flex items-start justify-center w-full m-0 p-0">
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${darkMode ? "bg-gray-800" : "bg-white"}`}
                  >
                    <AdminInvitations user={user} />
                  </div>
                </div>
              </div>
            )}

            {/* Admin Users Page */}
            {isAdmin && (
              <div
                className={currentPage === "admin-users" ? "block" : "hidden"}
              >
                <div className="flex items-start justify-center w-full m-0 p-0">
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${darkMode ? "bg-gray-800" : "bg-white"}`}
                  >
                    <AdminUsers user={user} darkMode={darkMode} />
                  </div>
                </div>
              </div>
            )}

            {/* Admin Messages Page */}
            {isAdmin && (
              <div className={currentPage === "messages" ? "block" : "hidden"}>
                <div className="flex items-start justify-center w-full m-0 p-0">
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${darkMode ? "bg-gray-800" : "bg-white"}`}
                  >
                    <Messages />
                  </div>
                </div>
              </div>
            )}

            {/* Admin Error Reports Page */}
            {isAdmin && (
              <div className={currentPage === "error-reports" ? "block" : "hidden"}>
                <div className="flex items-start justify-center w-full m-0 p-0">
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${darkMode ? "bg-gray-800" : "bg-white"}`}
                  >
                    <ErrorReports />
                  </div>
                </div>
              </div>
            )}

            {/* Doctor Pages */}
            {isDoctor && (
              <>
                {/* Patients Management */}
                <div
                  className="flex items-start justify-center w-full m-0 p-0"
                  style={{
                    display: currentPage === "patients" ? "flex" : "none",
                  }}
                >
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${darkMode ? "bg-gray-800" : "bg-white"}`}
                  >
                    <GoalsManagement user={user} darkMode={darkMode} />
                  </div>
                </div>

                {/* Patient Calendar View */}
                <div
                  className="flex items-start justify-center w-full m-0 p-0"
                  style={{
                    display:
                      currentPage === "patient-calendar" ? "flex" : "none",
                  }}
                >
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${darkMode ? "bg-gray-800" : "bg-white"}`}
                  >
                    <PatientCalendarView
                      key={`cal-${refreshKey}`}
                      user={user}
                      darkMode={darkMode}
                    />
                  </div>
                </div>

                {/* Patient Statistics View */}
                <div
                  className="flex items-start justify-center w-full m-0 p-0"
                  style={{
                    display: currentPage === "patient-stats" ? "flex" : "none",
                  }}
                >
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${darkMode ? "bg-gray-800" : "bg-white"}`}
                  >
                    <PatientStatisticsView
                      key={`stats-${refreshKey}`}
                      user={user}
                      darkMode={darkMode}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Log Progress Page */}
            <div
              className={
                currentPage === "log-progress"
                  ? "flex items-start justify-center w-full m-0 p-0"
                  : "hidden"
              }
            >
              <div
                className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                  darkMode
                    ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                    : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                } p-[30px] ${
                  darkMode ? "bg-gray-800" : "bg-white"
                }`}
              >
                <LogEntry
                  user={user}
                  darkMode={darkMode}
                  onLogAdded={() => {
                    setRefreshKey((prev) => prev + 1);
                  }}
                  treatmentType={profile?.treatment_type as "self" | "child"}
                />
              </div>
            </div>

            {/* Patient Pages */}
            {!isDoctor && (
              <>
                {/* Patient Calendar */}
                <div
                  className={
                    currentPage === "calendar"
                      ? "flex items-start justify-center w-full m-0 p-0"
                      : "hidden"
                  }
                >
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${
                      darkMode ? "bg-gray-800" : "bg-white"
                    }`}
                  >
                    <Calendar
                      user={user}
                      darkMode={darkMode}
                      key={`calendar-${refreshKey}`}
                    />
                  </div>
                </div>

                {/* Patient Statistics */}
                <div
                  className={
                    currentPage === "statistics"
                      ? "flex items-start justify-center w-full m-0 p-0"
                      : "hidden"
                  }
                >
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${
                      darkMode ? "bg-gray-800" : "bg-white"
                    }`}
                  >
                    <Statistics
                      user={user}
                      darkMode={darkMode}
                      key={`statistics-${refreshKey}`}
                    />
                  </div>
                </div>

                {/* Healthcare Team (Doctor Invitations) */}
                <div
                  className={
                    currentPage === "healthcare"
                      ? "flex items-start justify-center w-full m-0 p-0"
                      : "hidden"
                  }
                >
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] ${
                      darkMode ? "bg-gray-800" : "bg-white"
                    }`}
                  >
                    <DoctorInvitations user={user} darkMode={darkMode} />
                  </div>
                </div>
              </>
            )}

            {/* Dashboard Home */}
            <div
              className={
                currentPage === "dashboard"
                  ? "flex items-start justify-center w-full m-0 p-0"
                  : "hidden"
              }
            >
              <div className="flex items-start justify-center w-full m-0 p-0">
                {needsProfile ? (
                  <div
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.25)] p-[30px] flex flex-col items-center space-y-6 ${
                      darkMode ? "bg-gray-800" : "bg-white"
                    }`}
                  >
                    <h2
                      className={`text-2xl font-bold text-center ${
                        darkMode ? "text-white" : "text-brand-dark-blue"
                      }`}
                    >
                      Complete Your Profile
                    </h2>
                    <form
                      onSubmit={handleProfileSubmit}
                      className="w-full space-y-4"
                    >
                      <div>
                        <label
                          htmlFor="profile-name"
                          className={`block text-sm font-medium mb-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          First Name
                        </label>
                        <input
                          id="profile-name"
                          type="text"
                          maxLength={50}
                          value={profileForm.name}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              name: processNameInput(e.target.value),
                            })
                          }
                          required
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base ${
                            darkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "border-gray-300"
                          }`}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="profile-surname"
                          className={`block text-sm font-medium mb-2 ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Last Name
                        </label>
                        <input
                          id="profile-surname"
                          type="text"
                          maxLength={50}
                          value={profileForm.surname}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              surname: processNameInput(e.target.value),
                            })
                          }
                          required
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors placeholder-gray-400 text-base ${
                            darkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "border-gray-300"
                          }`}
                        />
                      </div>

                      {/* Daily Patching Goal */}
                      <div className="pt-2">
                        <h3
                          className={`text-lg font-bold mb-1 ${
                            darkMode ? "text-white" : "text-brand-dark-blue"
                          }`}
                        >
                          Daily Patching Goal
                        </h3>
                        <p
                          className={`text-sm mb-3 ${
                            darkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          How long would you like to patch each day? You can
                          adjust this and set rest days later in Settings.
                        </p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <label
                              htmlFor="profile-goal-hours"
                              className={`block text-xs font-medium mb-1 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Hours
                            </label>
                            <input
                              id="profile-goal-hours"
                              type="number"
                              min={0}
                              max={8}
                              value={profileGoalHours}
                              onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, "").slice(-1);
                                const hours = val === "" ? 0 : Math.min(parseInt(val, 10) || 0, 8);
                                setProfileGoalHours(hours === 0 ? "" : String(hours));
                              }}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors text-center text-lg font-semibold ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "border-gray-300"
                              }`}
                            />
                          </div>
                          <span
                            className={`text-xl font-bold mt-5 ${
                              darkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            :
                          </span>
                          <div className="flex-1">
                            <label
                              htmlFor="profile-goal-minutes"
                              className={`block text-xs font-medium mb-1 ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              Minutes
                            </label>
                            <input
                              id="profile-goal-minutes"
                              type="number"
                              min={0}
                              max={59}
                              value={profileGoalMinutes}
                              onChange={(e) => {
                                const cleaned = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                                const mins = cleaned === "" ? 0 : Math.min(parseInt(cleaned, 10) || 0, 59);
                                setProfileGoalMinutes(cleaned === "" ? "" : String(mins));
                              }}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-brand-cyan focus:border-transparent transition-colors text-center text-lg font-semibold ${
                                darkMode
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : "border-gray-300"
                              }`}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Treatment type defaults to "self" - can be changed in Settings */}

                      <button
                        type="submit"
                        disabled={profileLoading}
                        className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white font-bold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform-gpu hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base"
                      >
                        {profileLoading ? "Saving..." : "Save Profile"}
                      </button>
                      {profileStatus && (
                        <div
                          className={`p-3 rounded-lg text-sm font-medium mt-2 ${
                            profileStatus.startsWith("Profile updated")
                              ? "bg-green-50 text-green-800 border border-green-200"
                              : "bg-red-50 text-red-800 border border-red-200"
                          }`}
                        >
                          {profileStatus}
                        </div>
                      )}
                    </form>
                  </div>
                ) : (
                  <div
                    // Card height now accounts for navbar (64px). Adjust 64px if navbar height changes.
                    className={`w-full max-w-screen-lg min-h-[calc(100dvh-30px-50px-64px)] lg:mt-[30px] lg:ml-[70px] lg:mr-[70px] lg:mb-[50px] md:mt-4 md:ml-6 md:mr-6 md:mb-6 mt-2 ml-2 mr-2 mb-2 rounded-2xl ${
                      darkMode
                        ? "shadow-[0_12px_50px_rgba(0,0,0,0.6)] ring-1 ring-black/40"
                        : "shadow-[0_8px_40px_rgba(0,0,0,0.25)]"
                    } p-[30px] flex flex-col items-center space-y-6 ${
                      darkMode ? "bg-gray-800" : "bg-white"
                    }`}
                  >
                    <h1
                      className={`text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2 text-center ${
                        darkMode ? "text-white" : "text-brand-dark-blue"
                      }`}
                    >
                      Welcome,{" "}
                      {isDoctor
                        ? `Dr. ${profile?.name}`
                        : isAdmin
                        ? `Admin ${profile?.name}`
                        : profile?.name}
                      !
                    </h1>
                    <p
                      className={`text-lg ${
                        darkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      {isDoctor
                        ? "Welcome to your doctor dashboard. Manage your patients, set goals, and track their progress."
                        : isAdmin
                        ? "Welcome to your admin dashboard. Manage user invitations and view contact messages."
                        : ""}
                    </p>
                    <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {isDoctor ? (
                        <>
                          {/* Doctor Dashboard Content */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 col-span-1 lg:col-span-2">
                            <button
                              onClick={() => setCurrentPage("patients")}
                              className="bg-gradient-to-b from-brand-cyan to-brand-light-green rounded-xl shadow-lg p-5 flex flex-col items-center justify-center hover:scale-105 transition-all duration-300 ease-out transform-gpu min-h-[120px] w-full"
                            >
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                👥
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                Manage Patients
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                Set goals and track progress
                              </span>
                            </button>
                            <button
                              onClick={() => setCurrentPage("patient-calendar")}
                              className="bg-gradient-to-b from-brand-dark-green to-brand-pale-green rounded-xl shadow-lg p-5 flex flex-col items-center justify-center hover:scale-105 transition-all duration-300 ease-out transform-gpu min-h-[120px] w-full"
                            >
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                📅
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                Patient Calendar
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                View patient activity
                              </span>
                            </button>
                            <button
                              onClick={() => setCurrentPage("patient-stats")}
                              className="bg-gradient-to-b from-brand-dark-blue to-brand-cyan rounded-xl shadow-lg p-5 flex flex-col items-center justify-center hover:scale-105 transition-all duration-300 ease-out transform-gpu min-h-[120px] w-full"
                            >
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                📊
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                Patient Statistics
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                Progress tracking
                              </span>
                            </button>

                            <div className="bg-gradient-to-b from-brand-yellow to-brand-pale-green rounded-xl shadow-lg p-5 flex flex-col items-center justify-center min-h-[120px] w-full opacity-60">
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                📝
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                Patient Notes
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                Coming soon
                              </span>
                            </div>
                            <button
                              onClick={() => setCurrentPage("settings")}
                              className="bg-gradient-to-b from-brand-pale-green to-brand-light-green rounded-xl shadow-lg p-5 flex flex-col items-center justify-center hover:scale-105 transition-all duration-300 ease-out transform-gpu min-h-[120px] w-full"
                            >
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                ⚙️
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                Settings
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                Manage account
                              </span>
                            </button>
                          </div>
                        </>
                      ) : isAdmin ? (
                        <>
                          {/* Admin Dashboard Content */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 col-span-1 lg:col-span-2">
                            <button
                              onClick={() =>
                                setCurrentPage("admin-invitations")
                              }
                              className="bg-gradient-to-b from-brand-cyan to-brand-light-green rounded-xl shadow-lg p-5 flex flex-col items-center justify-center hover:scale-105 transition-all duration-300 ease-out transform-gpu min-h-[120px] w-full"
                            >
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                📧
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                Send Invitations
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                Invite new users
                              </span>
                            </button>
                            <button
                              onClick={() => setCurrentPage("messages")}
                              className="bg-gradient-to-b from-brand-dark-green to-brand-pale-green rounded-xl shadow-lg p-5 flex flex-col items-center justify-center hover:scale-105 transition-all duration-300 ease-out transform-gpu min-h-[120px] w-full"
                            >
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                💬
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                Contact Messages
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                View user inquiries
                              </span>
                            </button>
                            <button
                              onClick={() => setCurrentPage("error-reports")}
                              className="bg-gradient-to-b from-orange-500 to-red-500 rounded-xl shadow-lg p-5 flex flex-col items-center justify-center hover:scale-105 transition-all duration-300 ease-out transform-gpu min-h-[120px] w-full"
                            >
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                🐛
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                Error Reports
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                View error reports
                              </span>
                            </button>
                            <button
                              onClick={() => setCurrentPage("settings")}
                              className="bg-gradient-to-b from-brand-dark-blue to-brand-cyan rounded-xl shadow-lg p-5 flex flex-col items-center justify-center hover:scale-105 transition-all duration-300 ease-out transform-gpu min-h-[120px] w-full"
                            >
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                ⚙️
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                Settings
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                Manage account
                              </span>
                            </button>
                            <div className="bg-gradient-to-b from-brand-yellow to-brand-pale-green rounded-xl shadow-lg p-5 flex flex-col items-center justify-center min-h-[120px] w-full opacity-60">
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                📊
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                User Analytics
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                Coming soon
                              </span>
                            </div>
                            <div className="bg-gradient-to-b from-brand-cyan to-brand-dark-blue rounded-xl shadow-lg p-5 flex flex-col items-center justify-center min-h-[120px] w-full opacity-60">
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                👥
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                User Management
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                Coming soon
                              </span>
                            </div>
                            <div className="bg-gradient-to-b from-brand-pale-green to-brand-light-green rounded-xl shadow-lg p-5 flex flex-col items-center justify-center min-h-[120px] w-full opacity-60">
                              <span className="text-2xl sm:text-3xl font-bold text-white mb-2">
                                🔧
                              </span>
                              <span className="text-base sm:text-lg font-semibold text-white mb-1">
                                System Settings
                              </span>
                              <span className="text-white opacity-80 text-xs sm:text-sm">
                                Coming soon
                              </span>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Dashboard Content */}
                          <div className="w-full col-span-1 lg:col-span-2">
                            <ProgressDashboard
                              user={user}
                              darkMode={darkMode}
                              treatmentType={
                                (profile?.treatment_type as "self" | "child") ||
                                "self"
                              }
                              onNavigate={setCurrentPage}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
